import angular from 'angular';
import ComponentsModule from '../components-module';
import template from './code-inbox.html';
import Sortable from 'sortablejs';
import Debug from 'ace-ui/ace-debug';

const COMPONENT_NAME = 'codeInbox';
const SEARCH_INTERVAL = 20000;

const debug = new Debug (COMPONENT_NAME);

const SUBSCR = {
  CREATE: 'AI_CREATE',
  UPDATE: 'AI_UPDATE',
  DELETE: 'AI_DELETE'
};

const UPDATE_MODE = {
  EVENT: 'event',
  SEARCH: 'search',
  OFF: 'off'
};

/* global $ _ */
angular.module (ComponentsModule)
  .component (COMPONENT_NAME, {
    template,

    bindings: {
      activeMode: '<',
      codeConfig: '<',
      notifyUpdate: '&'
    },

    controller: function (
      $interval,
      $q,
      $rootScope,
      $scope,
      $timeout,
      Application,
      ApplicationConfigurationService,
      ClipboardService,
      ContentCardService,
      ContentEditor,
      ContentEventService,
      ContentService,
      ImageService,
      SearchService,
      UserDataService) {

      const throttleWaitMs = 1500;

      let ctrl = this;
      let processingQueue = [];
      let userContexts = undefined;

      ctrl.updateMode = UPDATE_MODE.OFF;

      ctrl.getConf = ApplicationConfigurationService
        .getConfigurationFor('ContentActivityInbox').then((conf) => {
          return Object.assign({ enabled: true }, conf);
        });

      function resolveUpdateMode () {
        if (!Application.currentUser.token) {
          return UPDATE_MODE.OFF;
        }

        if (ctrl.connected) {
          return UPDATE_MODE.EVENT;
        }

        return UPDATE_MODE.SEARCH;
      }

      function hasEntryFor (mainAlias) {
        return entryIndexFor(mainAlias) >= 0;
      }

      function entryIndexFor (mainAlias) {
        return ctrl.entries.findIndex ((i) => i.id === mainAlias);
      }

      function fetchEntry (version, revision) {
        return ContentService.getContentVersion({ version, variant: 'aceUISearch', atLeastRevision: revision })
          .then ((response) => toEntry (response.content, revision));
      }

      const contextsHasOwner = (contexts, owners) => {
        // If there are no user contexts, we always pass

        if ((contexts || []).length === 0) {
          return true;
        }

        // If there are user contexts, we only pass if at least one of
        // the contexts is matched against an owner

        return (contexts || []).filter(context => {
          return owners.indexOf(context) !== -1;
        }).length > 0;
      };

      const updateEntryList = _.throttle(() => {
        let newEntries = [];

        processingQueue.splice(-50).forEach((eventData) => {
          let mainAlias = eventData.system.mainAlias;
          let version = eventData.current.versionId;
          let revision = eventData.current.revision;
          let isHidden = eventData.current.metadata.hidden;

          if (isHidden) {
            let entryIndex = entryIndexFor(mainAlias);

            if(entryIndex >= 0) {
              ctrl.entries.splice(entryIndex, 1);
            }
          } else {
            let owners = eventData.current.metadata.owners || [];

            if (contextsHasOwner(userContexts, owners)) {
              newEntries.unshift(fetchEntry(version, revision));
            } else {
              let entryIndex = entryIndexFor(mainAlias);

              if(entryIndex >= 0) {
                ctrl.entries.splice(entryIndex, 1);
              }
            }
          }
        });

        debug.log ('newEntries: ' + newEntries.length);

        $q.all(newEntries)
          .then((entries) => {
            entries.forEach((entry) => {
              if (hasEntryFor(entry.id)) {
                ctrl.entries.splice(entryIndexFor(entry.id), 1);
              }

              ctrl.entries.unshift(entry);
              ctrl.notifyUpdate({ increment: 1 });
            });

            ctrl.entries = ctrl.entries.splice(0, 50);
          });

        processingQueue = [];
      }, throttleWaitMs);

      function initializeUserContexts () {
        if (userContexts !== undefined) {
          return $q.when();
        }

        return UserDataService.getUserContexts()
          .then(contexts => {
            userContexts = contexts;
          })
          .catch(error => {
            console.error('Error fetching user contexts for current user. No content events will be processed.', error);
          });
      }

      function toEntry (content, revision) {
        let searchResult = ContentCardService.fromAceUISearch (content);
        let entry = {
          key: content.system.version,
          id: content.system.id,
          revision
        };

        entry.contentCard = searchResult.getContextual({
          thumbnail: { aspectRatio: '1:1', width: 45 },
          datetime: { shortDateFormat: true }
        }, 'LIST', 'CONTENT-ACTIVITY');

        ContentCardService.resolveThumbnail(entry.contentCard.thumbnail)
          .then (thumbnail => { entry.contentCard.thumbnail = thumbnail; });

        return entry;
      }

      ctrl.setUpdateMode = (mode) => {
        debug.log ('setUpdateMode (\'' + mode + '\')');

        if (mode === ctrl.updateMode) {
          return;
        } else if (mode === UPDATE_MODE.OFF) {
          $interval.cancel(ctrl.searchIntervalId);
          ctrl.searchIntervalId = 0;
          ContentEventService.pause([SUBSCR.CREATE, SUBSCR.DELETE, SUBSCR.UPDATE]);
          ctrl.updateMode = UPDATE_MODE.OFF;
        } else if (mode === UPDATE_MODE.EVENT) {
          $interval.cancel(ctrl.searchIntervalId);
          ctrl.searchIntervalId = 0;
          ContentEventService.resume([SUBSCR.CREATE, SUBSCR.DELETE, SUBSCR.UPDATE]);
          ctrl.updateMode = UPDATE_MODE.EVENT;
        } else if (mode === UPDATE_MODE.SEARCH) {
          ContentEventService.pause([SUBSCR.CREATE, SUBSCR.DELETE, SUBSCR.UPDATE]);
          if (!ctrl.searchIntervalId) {
            ctrl.searchIntervalId = $interval(ctrl.refreshInbox, SEARCH_INTERVAL);
            ctrl.updateMode = UPDATE_MODE.SEARCH;
          }
        }
      };

      const inboxMainQuery = (userContexts) => {
        let contexts = userContexts || [];
        return contexts.length > 0 ? 'ace_security_context_ss:(' + contexts.join(' ') + ')' : '*:*';
      };

      const inboxContentTypeQuery = (contentTypes) => {
        let types = contentTypes || [];
        return types.length > 0 ? 'ace_type_s:(' + types.join(' ') + ')' : undefined;
      };

      ctrl.refreshInbox = () => {
        debug.log ('Refreshing inbox');

        const skipSearch = !ctrl.activeMode
        || (ctrl.codeConfig.contentTypes && ctrl.codeConfig.contentTypes.length === 0)
        || (ctrl.codeConfig.workflowStates && ctrl.codeConfig.workflowStates.length === 0);

        if (skipSearch) {
          debug.log ('Skip search.');
          return $q.resolve();
        }

        ctrl.refreshing && debug.log ('Refreshing in progress');

        if (!ctrl.refreshing) {
          let mainQuery = inboxMainQuery (userContexts);
          let filterQuery = inboxContentTypeQuery (ctrl.codeConfig.contentTypes);

          ctrl.refreshing = SearchService.search({
            params: {
              q: mainQuery,
              fq: filterQuery,

              sort: 'ace_modificationTime_dt desc'
            },

            variant: 'aceUISearch'
          })
            .then((searchResponse) => {
              ctrl.entries = searchResponse.response.docs
                .reduce ((acc, curr) => {
                // It can contain same content with different revisions...
                // This should not have to be done here. Smarter query? Deeper solution?
                  let existingIndex = acc.findIndex((doc) => doc.ace_mainAlias_s === curr.ace_mainAlias_s);

                  if (existingIndex === -1) {
                    acc.push(curr);
                  } else if (acc[existingIndex].aceRevision_l < curr.aceRevision_l) {
                    acc[existingIndex] = curr;
                  }

                  return acc;
                }, [])
                .map ((doc) => toEntry(doc._content));

              debug.log ('Inbox refreshed.');

              ctrl.refreshing = undefined;
            })
            .catch ((error) => {
              console.error('ERROR: refreshing ContentActivityInbox.', error);
              ctrl.refreshing = undefined;
            });
        }

        return ctrl.refreshing;
      };

      ctrl.copyEntry = (event, entry) => {
        $(event.currentTarget).blur();

        event.stopPropagation();
        event.preventDefault();
        ClipboardService.putInClipboard([
          {
            id: entry.alias,
            type: entry.contentType.value
          }
        ])
          .catch ((error) => {
            console.warn('Error: Failed to insert content reference(s) into clipboard!', error);
          });
      };

      ctrl.openEntry = (entry) => {
        ContentEditor.openContent(entry.id);
      };

      ctrl.$onInit = () => {
        ctrl.entries = [];
        ctrl.connected = false;

        let unsubscribers = [];

        debug.log ('$onInit, currentUser: ', Application.currentUser);

        $scope.$on('aceui:authenticationSuccess', () => {
          ctrl.setUpdateMode(resolveUpdateMode());

          if (ctrl.updateMode !== UPDATE_MODE.OFF) {
            initializeUserContexts()
              .then(() => (ctrl.refreshInbox()));
          }
        });

        $rootScope.$on('aceui:authenticationRequest', () => {
          ctrl.setUpdateMode (UPDATE_MODE.OFF);
        });

        $scope.$on('code:userLoggedOut', () => {
          ctrl.entries = [];
          ctrl.setUpdateMode (UPDATE_MODE.OFF);
        });

        $scope.$watch(() => ContentEventService.connected, (newValue) => {
          ctrl.connected = newValue;
          ctrl.setUpdateMode(resolveUpdateMode());
        });

        // Clarification: We are subscribing to all possible events, however,
        // we only add subscriptions to the ContentEventService for the configured
        // eventTypes (further down in $onChanges).
        initializeUserContexts()
          .then(() => {
            unsubscribers.push (ContentEventService.subscribe('AI_CREATE', (eventData) => {
              debug.log ('Processing CREATE event', eventData);
              processingQueue.push(eventData);
              updateEntryList();
            }));

            unsubscribers.push (ContentEventService.subscribe('AI_UPDATE', (eventData) => {
              debug.log ('Processing UPDATE event', eventData);
              processingQueue.push(eventData);
              updateEntryList();
            }));

            unsubscribers.push (ContentEventService.subscribe('AI_DELETE', (eventData) => {
              debug.log ('Processing DELETE event', eventData);
              $timeout(() => {
                if (hasEntryFor(eventData.mainAlias)) {
                  let entryIndex = entryIndexFor(eventData.mainAlias);
                  ctrl.entries.splice(entryIndex, 1);
                }
              });
            }));
          });

        Sortable.create($('.inbox-main-container ul')[0], {
          group: {
            name: 'content-group',
            pull: 'clone',
            put: false
          },

          sort: false,

          setData: (dt, el) => {
            dt.setData('contentId', $(el).data('contentid'));
            dt.setData('contentType', $(el).data('type'));
          }
        });

        ctrl.$onDestroy = () => {
          unsubscribers.forEach((unsubscribe) => {
            debug.log ('Unsubscribing');
            unsubscribe();
          });

          ctrl.setUpdateMode(UPDATE_MODE.OFF);
        };
      };

      ctrl.$onChanges = ({ activeMode, codeConfig }) => {
        
        if (activeMode) {
          ctrl.inboxFilterString = '';
          if (activeMode.currentValue) {
            debug.log ('Entering active mode');

            ctrl.setUpdateMode (resolveUpdateMode());

            if (ctrl.updateMode !== UPDATE_MODE.OFF) {
              initializeUserContexts()
                .then(() => (ctrl.refreshInbox()));
            }
          } else {
            debug.log ('Entering passive mode');
            ctrl.setUpdateMode (UPDATE_MODE.OFF);
            updateEntryList.cancel();
          }
        }

        if (codeConfig && codeConfig.currentValue) {
          let config = codeConfig.currentValue;
          let subscriptions = {};

          let eventTypes = config.eventTypes ? config.eventTypes : ['CREATE', 'UPDATE', 'DELETE'];

          eventTypes.forEach((eventType) => {
            subscriptions[SUBSCR[eventType]] = {
              eventType,
              system: {
                contentType: config.contentTypes
              }
            };
          });

          ContentEventService.addSubscriptions(subscriptions);
        }
      };
    }
  });

export default COMPONENT_NAME;
